/**
 * «Campañas», la lista única, en palabras: el nombre de cada mundo y su lista, el estado, las fechas, el envío y el
 * resultado de cada fila, los filtros de la URL y los tipos de campaña nueva. Puro y testeado (`campanas.test.ts`).
 */

import { fecha, fechaHora, num, plural } from "../../campana-nueva/utils/format";
import { pesos } from "../../envios/logic/resultado";
import { R_DESDE_PUBLICO, R_NUEVA_CAMPANA, R_SEGMENTACIONES } from "../../segmentaciones/logic/segmentar";
import type { Canal, EstadoCampana, FilaCampana, FiltrosCampanas, Mundo, Periodo, ResultadoCampana } from "../types";

export const R_CAMPANAS = "/activa/campanas";
/**
 * Elegir qué campaña armar: el único punto de entrada (el «Nueva campaña» del menú y el de la lista llevan acá). Estático,
 * le gana a `/activa/campanas/:id` (el detalle de una por QR, cuyos ids son de Mongo). Los asistentes de cada tipo siguen
 * en sus rutas (`/activa/segmentaciones/nueva` y los demás).
 */
export const R_NUEVA = `${R_CAMPANAS}/nueva`;

export const MUNDOS: Mundo[] = ["oficial", "segmentacion", "reactivacion", "mail"];
export const ESTADOS: EstadoCampana[] = ["borrador", "programada", "enviando", "pausada", "terminada", "cancelada"];
export const CANALES: Canal[] = ["whatsapp", "mail"];
export const PERIODOS: Periodo[] = ["todo", "30", "90", "365"];

/** Cada mundo, con su nombre y su propia lista (que sigue existiendo: se llega desde el filtro). */
export const MUNDO: Record<Mundo, { label: string; lista: string; textoLista: string; cls: string }> = {
  oficial: { label: "WhatsApp oficial", lista: "/activa/oficial/campanas", textoLista: "Abrir la lista del WhatsApp oficial", cls: "bg-emerald-50 text-emerald-800" },
  segmentacion: { label: "Con segmentación", lista: R_SEGMENTACIONES, textoLista: "Abrir la lista de campañas con segmentación", cls: "bg-violet-50 text-violet-800" },
  reactivacion: { label: "WhatsApp por QR", lista: "/activa", textoLista: "Abrir la lista de campañas por QR", cls: "bg-sky-50 text-sky-800" },
  mail: { label: "Mail (Snov)", lista: "/activa/emails/campanas", textoLista: "Abrir la lista de campañas de mail", cls: "bg-amber-50 text-amber-800" },
};

export const ESTADO: Record<EstadoCampana, { texto: string; cls: string }> = {
  borrador: { texto: "Borrador", cls: "bg-slate-100 text-slate-600" },
  programada: { texto: "Programada", cls: "bg-indigo-50 text-indigo-700" },
  enviando: { texto: "Enviando", cls: "bg-violet-50 text-violet-700" },
  pausada: { texto: "Pausada", cls: "bg-amber-50 text-amber-800" },
  terminada: { texto: "Terminada", cls: "bg-emerald-50 text-emerald-700" },
  cancelada: { texto: "Cancelada", cls: "bg-red-50 text-red-700" },
};

export const CANAL: Record<Canal, string> = { whatsapp: "WhatsApp", mail: "Mail" };

export const PERIODO: Record<Periodo, string> = { todo: "Desde siempre", "30": "Últimos 30 días", "90": "Últimos 90 días", "365": "Último año" };

/* ── Los filtros en la URL ───────────────────────────────────────────────────────────────── */

const de = <T extends string>(lista: readonly T[], v: string | null): T | undefined => (v && (lista as readonly string[]).includes(v) ? (v as T) : undefined);

/**
 * Los filtros que trae la URL (`?mundo=oficial&estado=enviando&q=octubre`). Un valor que no se conoce se ignora: un link
 * viejo o mal copiado abre la lista entera, no un error.
 */
export function filtrosDeLaUrl(sp: URLSearchParams): FiltrosCampanas {
  const pagina = Number(sp.get("pagina"));
  return {
    mundo: de(MUNDOS, sp.get("mundo")),
    estado: de(ESTADOS, sp.get("estado")),
    canal: de(CANALES, sp.get("canal")),
    periodo: de(PERIODOS, sp.get("periodo")),
    q: (sp.get("q") || "").slice(0, 100) || undefined,
    pagina: Number.isInteger(pagina) && pagina > 1 ? pagina : undefined,
  };
}

/** La URL de unos filtros: sin los vacíos, sin «desde siempre» ni la página 1. */
export function urlDeLosFiltros(f: FiltrosCampanas): URLSearchParams {
  const sp = new URLSearchParams();
  if (f.mundo) sp.set("mundo", f.mundo);
  if (f.estado) sp.set("estado", f.estado);
  if (f.canal) sp.set("canal", f.canal);
  if (f.periodo && f.periodo !== "todo") sp.set("periodo", f.periodo);
  if (f.q?.trim()) sp.set("q", f.q.trim());
  if (f.pagina && f.pagina > 1) sp.set("pagina", String(f.pagina));
  return sp;
}

export const hayFiltros = (f: FiltrosCampanas) => !!(f.mundo || f.estado || f.canal || (f.periodo && f.periodo !== "todo") || f.q?.trim());

/* ── Cada fila, en palabras ──────────────────────────────────────────────────────────────── */

/** Lo que dice su mundo además del estado común, cuando agrega algo («Faltan los nombres de los grupos»). */
export function detalleDelEstado(f: Pick<FilaCampana, "mundo" | "estadoDelMundo">): string | null {
  const e = f.estadoDelMundo;
  if (f.mundo === "oficial") return e === "pausada_auto" ? "Se pausó sola" : null;
  if (f.mundo === "segmentacion") {
    const textos: Record<string, string> = {
      procesando: "Armando los grupos",
      nombres_pendientes: "Faltan los nombres de los grupos",
      fallida: "No se pudo segmentar",
      borrador: "Falta preparar el envío",
      preparada: "Envío preparado: todavía no salió",
      midiendo: "Midiendo las compras",
      esperando_base: "Esperando la base nueva",
      terminada: "Medición terminada",
    };
    return textos[e] ?? null;
  }
  if (f.mundo === "reactivacion") return e === "vencida" ? "No salió a horario" : e === "measured" ? "Medida" : null;
  return e === "otro" ? "Otro estado en Snov" : null;
}

/** «Sale el 30/10/26 19:00», «Salió el 2 oct 2026 · terminó el 3 oct 2026» o «Creada el …». */
export function textoFechas(f: Pick<FilaCampana, "estado" | "estadoDelMundo" | "fechas">): string | null {
  const { creada, programada, empezo, termino } = f.fechas;
  if (programada && f.estado === "programada") return `Sale el ${fechaHora(programada)}`;
  if (programada && f.estadoDelMundo === "vencida") return `Esperaba salir el ${fechaHora(programada)}`;
  if (empezo) return `Salió el ${fecha(empezo)}${termino ? ` · terminó el ${fecha(termino)}` : ""}`;
  if (creada) return `Creada el ${fecha(creada)}`;
  return null;
}

/** «120 enviados · 110 entregados»; sin entregados si su mundo no lo sabe; nada si no salió. */
export function textoEnvio(f: Pick<FilaCampana, "enviados" | "entregados">): string | null {
  if (f.enviados === null) return null;
  const enviados = `${num(f.enviados)} ${plural(f.enviados, "enviado", "enviados")}`;
  return f.entregados === null ? enviados : `${enviados} · ${num(f.entregados)} ${plural(f.entregados, "entregado", "entregados")}`;
}

const porcentaje = (x: number) => `${(x * 100).toLocaleString("es-AR", { maximumFractionDigits: 1 })} %`;

/**
 * El resultado en un renglón, con las mismas palabras que su detalle:
 * - recompra: «6 volvieron a comprar (10 %) · $42.000»;
 * - contra el control: «Vendiste de más $419.000 · 92 compraron de 920 · facturaron los que la recibieron $940.000», y
 *   sin firmeza «≈ $0» (como `cifrasDeLaCampana`). Primero lo que trajo la campaña; lo bruto, con su nombre: si no, el
 *   primer número que se lee es lo que facturaron (que habrían comprado igual en buena parte).
 * Si el número todavía puede crecer, lo dice.
 */
export function textoResultado(r: ResultadoCampana): { texto: string; hay: boolean } {
  if (!r.disponible) return { texto: r.texto, hay: false };
  const crece = r.parcial ? " · todavía puede crecer" : "";
  if (r.tipo === "contra-control") {
    const de = r.recibieron !== null ? ` de ${num(r.recibieron)}` : "";
    const deMas = r.firme && (r.vendisteDeMas ?? 0) > 0 ? pesos(r.vendisteDeMas ?? 0) : "≈ $0";
    return { texto: `Vendiste de más ${deMas} · ${num(r.volvieron)} ${plural(r.volvieron, "compró", "compraron")}${de} · facturaron los que la recibieron ${pesos(r.plata)}${crece}`, hay: true };
  }
  const tasa = r.tasa !== null ? ` (${porcentaje(r.tasa)})` : "";
  return { texto: `${num(r.volvieron)} ${plural(r.volvieron, "volvió", "volvieron")} a comprar${tasa} · ${pesos(r.plata)}${crece}`, hay: true };
}

/* ── Campaña nueva ───────────────────────────────────────────────────────────────────────── */

export interface OpcionNueva {
  /** único: la key y el testid de la tarjeta (dos opciones pueden ser del mismo mundo) */
  id: string;
  mundo: Mundo;
  titulo: string;
  texto: string;
  /** cuándo conviene esta opción y no otra (29/10/2026) */
  conviene: string;
  ruta: string;
}

/**
 * Los asistentes que ya existen, como los muestra «Nueva campaña» (`R_NUEVA`), cada uno con cuándo conviene. «Con los
 * grupos de tu Público» (01/11/2026) es del mismo mundo que «Con segmentación»: por eso cada opción tiene su `id`. Las de
 * mail se arman en Snov (o salen con una con segmentación). Debajo, «Empezar de una anterior» copia una del WhatsApp
 * oficial (`EmpezarDeUnaAnterior`).
 */
export const OPCIONES_NUEVA: OpcionNueva[] = [
  {
    id: "segmentacion",
    mundo: "segmentacion",
    titulo: "Con segmentación",
    texto: "DEENEX parte tu base en grupos, escribe un mensaje para cada uno y deja un grupo de control para medir cuánto vendiste de más.",
    conviene: "Conviene si querés saber cuánto vendiste de más gracias a la campaña.",
    ruta: R_NUEVA_CAMPANA,
  },
  {
    // 01/11/2026: una campaña con segmentación que no vuelve a correr el motor
    id: "desde-publico",
    mundo: "segmentacion",
    titulo: "Con los grupos de tu Público",
    texto: "Usás los grupos que ya armó DEENEX con tu base, sin volver a segmentar. Cada grupo elige su canal y su mensaje.",
    conviene: "Conviene si ya segmentaste y querés mandarles otra cosa a los mismos grupos, con un control nuevo para medir.",
    ruta: R_DESDE_PUBLICO,
  },
  {
    id: "oficial",
    mundo: "oficial",
    titulo: "WhatsApp oficial",
    texto: "Una plantilla aprobada por Meta, a un público de tu base o a los grupos de un análisis de ventas.",
    conviene: "Conviene para mandar a muchas personas desde el número oficial de la marca, sin usar tus líneas.",
    ruta: "/activa/oficial/campanas/nueva",
  },
  {
    id: "reactivacion",
    mundo: "reactivacion",
    titulo: "WhatsApp por QR",
    texto: "Subís tus pedidos y tus clientes, y sale por tus líneas de WhatsApp y tus casillas de mail.",
    conviene: "Conviene si tenés los pedidos y los clientes en archivos y querés mandar por tus propias líneas o por mail.",
    ruta: "/activa/real",
  },
];

export const TEXTO_NUEVA_MAIL = "Las campañas de mail se arman en Snov (o salen con una campaña con segmentación) y aparecen acá solas.";
